import { styled } from '@mui/material/styles';
import {
  Box,
  Step,
  StepConnector,
  StepIconProps,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import stepConnectorClasses from '@mui/material/StepConnector/stepConnectorClasses';
import PostAttachments from 'components/modals/addPostModal/PostAttachments';
import {
  ArticleSourceType,
  ArticleType,
  AttachmentType,
} from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { useFormContext, useWatch } from 'react-hook-form';
import { PostFormData } from '@buildwise/pages/addPost/hooks/useAddPostForm';
import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { ReactComponent as UploadFiles } from 'assets/icons/UploadFiles.svg';
import { ReactComponent as NotesPanel } from 'assets/icons/NotesPanel.svg';
import { ReactComponent as SearchNotes } from 'assets/icons/SearchNotes.svg';

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 2,
    border: 0,
    margin: '0 16px',
    backgroundColor: theme.palette.custom.blueChalk,
    borderRadius: 1,
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },
}));

const ColorlibStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme }) => ({
  backgroundColor: theme.palette.custom.blueChalk,
  zIndex: 1,
  color: theme.palette.custom.tuna,
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  ...theme.applyStyles('dark', {
    backgroundColor: theme.palette.custom.tuna,
  }),
  variants: [
    {
      props: ({ ownerState }) => ownerState.active,
      style: {
        boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
      },
    },
    {
      props: ({ ownerState }) => ownerState.completed,
    },
  ],
}));

function ColorlibStepIcon(props: StepIconProps) {
  const { active, completed, className } = props;

  const icons: { [index: string]: React.ReactElement<unknown> } = {
    1: <Typography fontWeight={700}>1</Typography>,
    2: <Typography fontWeight={700}>2</Typography>,
    3: <Typography fontWeight={700}>3</Typography>,
  };

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
      {icons[String(props.icon)]}
    </ColorlibStepIconRoot>
  );
}

type ParseFileFormProps = {
  article: ArticleType | undefined;
  articleType?: ArticleSourceType;
  isFullScreen?: boolean;
  loadingFiles: boolean;
  response: any;
  downloadedFiles: AttachmentType[];
  onDrop: (acceptedFiles: File[]) => void;
  setDownloadedFiles: Dispatch<SetStateAction<AttachmentType[]>>;
};

function WeWillWriteForm({
  article,
  articleType,
  isFullScreen,
  loadingFiles,
  onDrop,
}: ParseFileFormProps) {
  const { resetField } = useFormContext<PostFormData>();
  const { control } = useFormContext<PostFormData>();
  const attachments = useWatch({
    control,
    name: 'attachments',
    defaultValue: [],
  });
  const steps = [
    {
      title: 'Drop Your Files',
      icon: <UploadFiles height={180} />,
    },
    {
      title: 'AI Updates The Knowledge Base',
      icon: <NotesPanel height={180} />,
    },
    {
      title: 'Easily Searchable By Others',
      icon: <SearchNotes height={180} />,
    },
  ];
  const attachmentsContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    resetField('attachments');
  }, []);

  useEffect(() => {
    if (attachmentsContainerRef.current && attachments.length) {
      const thumbnailsRef = attachmentsContainerRef.current.querySelector('#thumbnails');
      if (thumbnailsRef) {
        thumbnailsRef.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      }
    }
  }, [attachmentsContainerRef.current, attachments]);

  return (
    <Box minHeight={600} mt={2}>
      <Stepper alternativeLabel activeStep={-1} connector={<ColorlibConnector />} sx={{ my: 3 }}>
        {steps.map((item) => (
          <Step key={item.title}>
            <StepLabel
              StepIconComponent={ColorlibStepIcon}
              sx={(theme) => ({
                '& .MuiStepLabel-label': {
                  fontWeight: 700,
                  fontSize: 14,
                  color: theme.palette.custom.tuna,
                },
              })}
            >
              {item.title}
            </StepLabel>
            <Box display="flex" alignItems="center" justifyContent="center" mt={4}>
              {item.icon}
            </Box>
          </Step>
        ))}
      </Stepper>
      <Box margin="40px auto 0" ref={attachmentsContainerRef}>
        <PostAttachments
          isFullScreen={isFullScreen}
          articleType={articleType}
          article={article}
          onDrop={onDrop}
          loadingFiles={loadingFiles}
          dropzoneSx={(theme) => ({
            height: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `1px dashed ${theme.palette.custom.coolGrey}`,
          })}
        />
      </Box>
    </Box>
  );
}

export default WeWillWriteForm;
